// @givmo/app-charity — the ONE place this console binds itself to `@givmo/org-console`
// (CBO-CT1 slice 6, re-homed onto the org pages by CPA-WEB40).
//
// The mount supplies six things and nothing else: WHICH organization (`activeCharityId`), WHAT this
// host calls things (`charityOrgConsoleVocab` — the entity class as DATA), WHO is signed in (so the
// editor can warn someone about demoting themselves), WHERE the per-module door goes, WHICH app is
// the class default (`CLASS_DEFAULT_SLUG` off the class table — the Apps page leads with its card,
// D-CP-54), and WHAT to re-read when a preference it draws from is written (below). That is the
// whole seam, and it is why this is a mount rather than a fork.
//
// ⚠ THE SCOPE IS `activeCharityId`, AND THE MOUNT WAITS FOR IT. The active-org context is already the
// single source of "which organization am I managing", is multi-org aware, and FAILS CLOSED — its
// `isReady` is true only once the read succeeded AND an active membership exists. Mounting the
// console before then would fire every read against an empty scope.
//
// ⚠ SIX MOUNTS, ONE CONFIG, ONE CACHE. `/org`, `/org/team-access`, `/org/activity`, `/settings`,
// every module's Team page and the shell's request dialog each build this independently, and they
// must not diverge: every key `@givmo/org-console` uses is scoped by `orgRef` alone, so every mount
// shares one cache entry per read. The standing read itself is the console-wide one
// (`console/charity-console-context.tsx`) — the panels join it on the same key, never a second query.
//
// ⚠ NO `onAccessWritten`. The company console needs that seam because its app grid is filtered by a
// read the package cannot see (`/admin/company/me`). This console's grid reads the package's OWN
// `self` key, which `accessWriteInvalidations` already refreshes after every write.
//
// ⚠ `onPreferencesWritten` IS SUPPLIED, for one key. The Compliance pages say when nobody gets
// deadline reminders, off the module's profile read (CCM-R1-REMIND-WEB), and the viewer's own
// "Email me Compliance reminders" switch moves that answer. So a write of that key RESETS the
// profile query rather than invalidating it: an invalidated entry keeps the answer the write just
// made stale, and the Dates page would paint "nobody gets reminders" from it on return until the
// re-read landed. Reset drops it, so the page shows no notice until the fresh answer says so.
//
// ⚠ THE TEST-ID PREFIX IS `org-console`, APP-WIDE, AND IS AN E2E CONTRACT (the authored-ahead
// `charity_portal.org_console.spec.ts` selects on it). Treat a change to it like an API change.

import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { usersGetMeOptions } from '@givmo/api-client/query';
import { COMPLIANCE_REMINDERS_PREFERENCE_KEY, complianceProfileQueryKey } from '@givmo/compliance';
import { servedModuleSlugs, type OrgConsoleConfig } from '@givmo/org-console';
import { ORG_ROUTE, TEAM_ACCESS_ROUTE } from '../../console/console-routes';
import {
  CLASS_DEFAULT_SLUG,
  PAGELESS_MODULE_SLUGS,
  charityConsoleTable,
  liveOrigin,
} from '../../console/module-table';
import { useModuleNavigate } from '../../console/use-console-squares';
import { useActiveOrg } from '../../org/active-org-context';
import { charityOrgConsoleVocab } from './org-console-vocab';

/** The test-id namespace every org-console panel in this app renders under. */
export const ORG_CONSOLE_TEST_ID_PREFIX = 'org-console';

/**
 * The console's configuration, or `null` while the active organization has not resolved.
 *
 * `null` is a LOADING state, never an error: the caller renders a quiet line and the console mounts
 * as soon as the memberships read settles.
 */
export function useOrgConsoleConfig(): OrgConsoleConfig | null {
  const { activeMembership, activeCharityId, isReady } = useActiveOrg();
  const navigate = useNavigate();

  // The caller's BACKEND-internal user id. Deliberately not the Firebase uid — every roster row is
  // keyed by the backend id, and the two id-spaces never coincide, so comparing a uid would silently
  // never match and the editor would stop warning someone that the row they are demoting is theirs.
  const meQuery = useQuery(usersGetMeOptions());
  const selfUserId = meQuery.data?.id ?? null;

  /**
   * The per-module door out of the apps grid: "who can open this one" is the roster narrowed to it.
   *
   * ⚠ THE HREF AND THE HOP ARE BOTH SUPPLIED, and neither is redundant. The package renders a real
   * link so copy-link and open-in-new-tab work; the callback keeps a click inside the SPA instead of
   * reloading the whole console. The package has no router of its own, deliberately.
   */
  const moduleAccessHref = useCallback(
    (module: string) => `${TEAM_ACCESS_ROUTE}?module=${encodeURIComponent(module)}`,
    [],
  );
  const onOpenModuleAccess = useCallback(
    (module: string) => void navigate({ to: TEAM_ACCESS_ROUTE, search: { module } }),
    [navigate],
  );
  /**
   * The generic in-app hop for the links shell contract v3 added (`links.apps`, the "Turn on <app>
   * first" door; `links.teamAccess`, the pending-requests strip). Same rule: the link stays a real
   * link, and this keeps a plain left-click inside the SPA.
   */
  const onNavigate = useModuleNavigate();

  const queryClient = useQueryClient();
  const onPreferencesWritten = useCallback(
    (keys: readonly string[]) => {
      if (activeCharityId === null || !keys.includes(COMPLIANCE_REMINDERS_PREFERENCE_KEY)) return;
      void queryClient.resetQueries({ queryKey: complianceProfileQueryKey(activeCharityId) });
    },
    [activeCharityId, queryClient],
  );

  const orgName = activeMembership?.name ?? '';

  return useMemo(
    () =>
      isReady && activeCharityId !== null
        ? {
            orgRef: activeCharityId,
            vocab: charityOrgConsoleVocab(orgName),
            selfUserId,
            // The class default LEADS the Apps page's cards (D-CP-54); the table's ONE flagged row.
            classDefault: CLASS_DEFAULT_SLUG,
            // The apps a person can open here — the grid's set, off the same table (OM-95), plus
            // Connected AI, which has no page and is listed wherever the catalog carries it.
            servedModules: servedModuleSlugs(
              charityConsoleTable(),
              liveOrigin(),
              PAGELESS_MODULE_SLUGS,
            ),
            testIdPrefix: ORG_CONSOLE_TEST_ID_PREFIX,
            links: {
              moduleAccess: moduleAccessHref,
              apps: ORG_ROUTE,
              teamAccess: TEAM_ACCESS_ROUTE,
            },
            onOpenModuleAccess,
            onNavigate,
            onPreferencesWritten,
          }
        : null,
    [
      isReady,
      activeCharityId,
      orgName,
      selfUserId,
      moduleAccessHref,
      onOpenModuleAccess,
      onNavigate,
      onPreferencesWritten,
    ],
  );
}
